import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  IconButton,
  InputAdornment,
  SvgIcon,
  Typography,
} from '@mui/material';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button from '@Components/Button';
import {
  CheckboxWrapper,
  TextFieldWrapper,
} from '@Components/FormFieldsWrappers';
import { FullScreenLoader } from '@Components/index';
import SMTPSettingsNotVerifiedModal from '@Components/SMTPSettingsNotVerifiedModal';
import SMTPSettingsVerificationModal from '@Components/SMTPSettingsVerificationModal';
import Icons from '@Constants/Icons.constants';
import { CommonConstants } from '@Constants/index';
import {
  SMTPSettingsFormInputs,
  SMTPSettingsProps,
} from '@Containers/Emails/SMTPSettings/SMTPSettings.types';
import { SMTPSettingsSchema } from '@Containers/Emails/SMTPSettings/SMTPSettingsSchema';
import {
  useAddSMTPSettingsMutation,
  useEditSMTPSettingsMutation,
  useGetSMTPSettingsQuery,
  useSendSMTPSettingsVerificationEmailMutation,
} from '@Services/smtpSettings.service';
import query from '@Utils/common/query.utils';
import useAlert from '@Utils/hooks/useAlert.hook';

const SMTPSettings: FunctionComponent<SMTPSettingsProps> = ({
  setAlertState,
}: SMTPSettingsProps): JSX.Element => {
  const { t } = useTranslation();
  const methods = useForm({
    resolver: yupResolver(SMTPSettingsSchema()),
    mode: 'onChange',
  });
  const { triggerAlert } = useAlert();

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isVerificationModalOpen, setIsVerificationModalOpen] =
    useState<boolean>(false);
  const [isNotVerifiedModalOpen, setIsNotVerifiedModalOpen] =
    useState<boolean>(false);
  const [isSentAgain, setSentAgain] = useState<boolean>(false);
  const [sendBcc, setSendBcc] = useState<boolean>(false);

  const userId = query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID) || null;
  const {
    data: smtpSettings,
    isLoading,
    isFetching,
  } = useGetSMTPSettingsQuery(userId);
  const [addSMTPSettings, addSMTPSettingsMethods] =
    useAddSMTPSettingsMutation();
  const [editSMTPSettings, editSMTPSettingsMethods] =
    useEditSMTPSettingsMutation();
  const [sendVerificationEmail, sendVerificationEmailMethods] =
    useSendSMTPSettingsVerificationEmailMutation();

  const loading =
    isLoading ||
    isFetching ||
    addSMTPSettingsMethods.isLoading ||
    editSMTPSettingsMethods.isLoading ||
    sendVerificationEmailMethods.isLoading;

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const onSubmitHandler: SubmitHandler<SMTPSettingsFormInputs> = (data) => {
    if (smtpSettings && smtpSettings.id) {
      return editSMTPSettings({
        ...data,
        id: smtpSettings.id,
        accountId: smtpSettings.accountId,
        sendBcc,
      });
    }
    return addSMTPSettings({
      ...data,
      accountId: userId ? userId.toString() : '',
      sendBcc,
    });
  };

  const handleSendEmail = useCallback(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    (): void => sendVerificationEmail(),
    [sendVerificationEmail]
  );

  const handleSendAgain = (): void => {
    handleSendEmail();
    setSentAgain(true);
  };

  useEffect(() => {
    if (smtpSettings) {
      methods.reset({
        fromEmail: smtpSettings.fromEmail,
        fromName: smtpSettings?.fromName || '',
        username: smtpSettings.username,
        hostname: smtpSettings.hostname,
        password: smtpSettings.password,
        port: smtpSettings.port,
      });
      setSendBcc(smtpSettings.sendBcc);
    }
  }, [methods, smtpSettings]);

  useEffect(() => {
    if (
      (addSMTPSettingsMethods.isSuccess || editSMTPSettingsMethods.isSuccess) &&
      !userId
    ) {
      handleSendEmail();
    }
    // eslint-disable-next-line
  }, [
    addSMTPSettingsMethods.isSuccess,
    editSMTPSettingsMethods.isSuccess,
    userId,
  ]);

  useEffect(() => {
    if (sendVerificationEmailMethods.isError && setAlertState) {
      const { error } = sendVerificationEmailMethods;
      if ('data' in error) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        triggerAlert(error.data.message, setAlertState, 'error');
      }
    }
  }, [
    sendVerificationEmailMethods,
    sendVerificationEmailMethods.isError,
    setAlertState,
    triggerAlert,
  ]);

  useEffect(() => {
    if (sendVerificationEmailMethods.isSuccess && setAlertState) {
      setIsVerificationModalOpen(true);
    }
  }, [sendVerificationEmailMethods.isSuccess, setAlertState, triggerAlert]);

  useEffect(() => {
    if (isSentAgain && setAlertState) {
      triggerAlert(
        'Verification email has been resent',
        setAlertState,
        'success'
      );
      setSentAgain(false);
    }
  }, [isSentAgain, setAlertState, triggerAlert]);

  useEffect(() => {
    if (setAlertState && smtpSettings && !smtpSettings.verified) {
      setIsNotVerifiedModalOpen(true);
    }
  }, [smtpSettings, setAlertState, triggerAlert]);

  return !isLoading ? (
    <>
      <SMTPSettingsVerificationModal
        isModalOpen={isVerificationModalOpen}
        setIsModalOpen={setIsVerificationModalOpen}
        email={smtpSettings?.fromEmail}
        onClick={handleSendAgain}
      />
      <SMTPSettingsNotVerifiedModal
        isModalOpen={isNotVerifiedModalOpen}
        setIsModalOpen={setIsNotVerifiedModalOpen}
        onClick={handleSendAgain}
      />
      <Box
        sx={{
          border: '1px solid #E0E0E0',
          borderRadius: '4px',
          padding: '16px',
        }}
      >
        <FormProvider {...methods}>
          <Box
            component="form"
            onSubmit={methods.handleSubmit(onSubmitHandler)}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                columnGap: '24px',
                pt: 2,
              }}
            >
              <Box>
                <Typography variant="input" fontWeight="bold" sx={{ pb: 1 }}>
                  {`${t('SMTP.fromEmail')}:`}
                </Typography>
                <TextFieldWrapper
                  name="fromEmail"
                  size="small"
                  placeholder={t('SMTP.enterEmail')}
                  sx={{ minWidth: '315px' }}
                />
              </Box>
              <Box>
                <Typography variant="input" fontWeight="bold" sx={{ pb: 1 }}>
                  {`${t('SMTP.fromName')}:`}
                </Typography>
                <TextFieldWrapper
                  name="fromName"
                  size="small"
                  placeholder={t('SMTP.enterFromName')}
                  sx={{ minWidth: '315px' }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                columnGap: '24px',
                pt: 2,
              }}
            >
              <Box>
                <Typography variant="input" fontWeight="bold" sx={{ pb: 1 }}>
                  {`${t('SMTP.username')}:`}
                </Typography>
                <TextFieldWrapper
                  name="username"
                  size="small"
                  placeholder={t('SMTP.enterUsername')}
                  sx={{ minWidth: '315px' }}
                />
              </Box>
              <Box>
                <Typography variant="input" fontWeight="bold" sx={{ pb: 1 }}>
                  {`${t('SMTP.hostname')}:`}
                </Typography>
                <TextFieldWrapper
                  name="hostname"
                  size="small"
                  placeholder={t('SMTP.enterHostname')}
                  sx={{ minWidth: '315px' }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                columnGap: '24px',
                pt: 2,
              }}
            >
              <Box>
                <Typography variant="input" fontWeight="bold" sx={{ pb: 1 }}>
                  {`${t('SMTP.password')}:`}
                </Typography>
                <TextFieldWrapper
                  name="password"
                  size="small"
                  placeholder={t('SMTP.enterPassword')}
                  type={showPassword ? 'text' : 'password'}
                  sx={{ minWidth: '315px' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ m: '0' }}>
                        <IconButton
                          sx={{ m: '0', mx: '0.4' }}
                          onClick={handleClickShowPassword}
                        >
                          <SvgIcon>
                            {showPassword
                              ? Icons.visibility
                              : Icons.visibilityOff}
                          </SvgIcon>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box>
                <Typography variant="input" fontWeight="bold" sx={{ pb: 1 }}>
                  {`${t('SMTP.port')}:`}
                </Typography>
                <TextFieldWrapper
                  name="port"
                  size="small"
                  placeholder={t('SMTP.enterPort')}
                  sx={{ minWidth: '315px' }}
                />
              </Box>
            </Box>
            <Box sx={{ mt: 2 }}>
              <CheckboxWrapper
                name="sendBcc"
                label="Automatically BCC all sent emails to this address"
                disabled={loading}
                checked={sendBcc}
                initialValue={sendBcc}
                onChange={(e, value) => {
                  console.log(value);
                  setSendBcc((prevState) => !prevState);
                }}
              />
            </Box>
            <Box>
              <Button
                type="submit"
                color="primary"
                sx={{ mt: 2, minWidth: '85px' }}
                disabled={loading}
              >
                {t('SMTP.submitText')}
              </Button>
            </Box>
          </Box>
        </FormProvider>
      </Box>
    </>
  ) : (
    <FullScreenLoader />
  );
};

export default SMTPSettings;
